import { Component, OnInit, OnDestroy, inject, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, combineLatest, of, timer } from 'rxjs';
import { takeUntil, map, switchMap } from 'rxjs/operators';

import { Icon } from '../../../core/models/icon.model';
import { CartFacade } from '../../../core/store/cart/cart.facade';
import { CartItem } from '../../../core/store/cart/cart.state';
import { AvailableOffersFacade } from '../../../core/store/available-offers/available-offers.facade';
import { ProductV2 } from '../../../core/models/product-v2.model';
import { CategoryIconMapperService } from '../../../shared/services/category-icon-mapper.service';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { CardWrapperComponent } from '../../../shared/components/card-wrapper/card-wrapper.component';
import { MultiSelectInputComponent, MultiSelectItem } from '../../../shared/components/multi-select-input/multi-select-input.component';
import { I18nPipe } from '../../../shared/pipes/i18n.pipe';
import { I18nService } from '../../../core/services/i18n.service';

interface ProductLinkData {
  cartItemId: string;
  productUuid: string;
  productName: string;
  planName: string;
  icon: Icon;
  selectedMsisdns: MultiSelectItem[];
  subscriptionCount: number;
  variationUuid: string;
  availableMsisdns: MultiSelectItem[];
}

@Component({
  selector: 'umkt-product-link-card',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    CardWrapperComponent,
    MultiSelectInputComponent,
    I18nPipe
  ],
  templateUrl: './product-link-card.component.html',
  styleUrls: ['./product-link-card.component.scss'],
})
export class ProductLinkCardComponent implements OnInit, OnDestroy {
  private cartFacade = inject(CartFacade);
  private availableOffersFacade = inject(AvailableOffersFacade);
  private categoryMapper = inject(CategoryIconMapperService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();
  private i18nService = inject(I18nService);

  @Input() canGoToPreviousStep = false;
  @Input() canGoToNextStep = false;
  @Input() isEditing = true;
  @Input() isLastStep = false;
  @Input() navigateToLinkingStepOnEdit = false;
  @Input() showAutoLinkWarning = false;
  @Output() previousStep = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() navigateToLinkingStep = new EventEmitter<void>();
  @Output() editingStateChange = new EventEmitter<boolean>();

  public products: ProductLinkData[] = [];
  public isLoading = false;
  public loadingText = '';
  
  private originalSelectedMsisdns: Record<string, MultiSelectItem[]> = {};

  ngOnInit(): void {
    this.loadCartProducts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCartProducts(): void {
    this.cartFacade.getCartItems()
      .pipe(takeUntil(this.destroy$))
      .subscribe((cartItems: CartItem[]) => {
        this.mapCartItemsToProducts(cartItems);
      });
  }

  private mapCartItemsToProducts(cartItems: CartItem[]): void {
    this.ensureCartItemsMappedToOffers(cartItems);
    
    timer(200).pipe(
      takeUntil(this.destroy$),
      switchMap(() => {
        const productsWithMsisdns$ = cartItems.map((item) => {
          const variationUuid = item.selectedVariation?.uuid;
          
          if (!variationUuid) {
            console.warn('[ProductLinkCard] Variation UUID não encontrado para item:', item.id);
            return of(this.createProductLinkData(item, []));
          }

          return this.availableOffersFacade.getMsisdnsByVariationUuid(variationUuid).pipe(
            map(msisdns => {
              const availableMsisdns = msisdns.map(msisdn => ({
                id: msisdn,
                label: this.formatMsisdn(msisdn)
              }));

              return this.createProductLinkData(item, availableMsisdns);
            })
          );
        });

        // Combina todos os observables e atualiza products quando todos estiverem prontos
        if (productsWithMsisdns$.length > 0) {
          return combineLatest(productsWithMsisdns$);
        } else {
          return of([]);
        }
      })
    ).subscribe(products => {
      this.products = products;
    });
  }
  
  private ensureCartItemsMappedToOffers(cartItems: CartItem[]): void {
    if (cartItems.length === 0) return;
    
    const productsMap = new Map<string, ProductV2>();
    
    cartItems.forEach(item => {
      if (item.product && !productsMap.has(item.product.uuid)) {
        productsMap.set(item.product.uuid, item.product);
      }
    });

    productsMap.forEach(product => {
      this.availableOffersFacade.mapProductVariationsToOffers(product);
    });
  }

  private createProductLinkData(item: CartItem, availableMsisdns: MultiSelectItem[]): ProductLinkData {
    const icon = this.categoryMapper.mapProductToIcon(
      item.product.categories || [],
      item.product.productType
    );

    const subscriptionCount = item.quantity;

    const selectedMsisdns: MultiSelectItem[] = [];
    if (item.linkedMsisdns && item.linkedMsisdns.length > 0) {
      selectedMsisdns.push(...item.linkedMsisdns.map(msisdn => {
        const existingLine = availableMsisdns.find(line => {
          const lineId = String(line.id);
          return lineId === msisdn || 
            lineId.replace(/\D/g, '') === msisdn.replace(/\D/g, '') ||
            line.label.replace(/\D/g, '') === msisdn.replace(/\D/g, '');
        });
        if (existingLine) {
          return existingLine;
        }
        // Se não encontrar, criar um novo item com o MSISDN como label
        return { id: msisdn, label: this.formatMsisdn(msisdn) };
      }));
    }

    return {
      cartItemId: item.id,
      productUuid: item.product.uuid,
      productName: item.product.name || 'Produto',
      planName: item.selectedVariation?.name || '',
      icon,
      selectedMsisdns,
      subscriptionCount,
      variationUuid: item.selectedVariation?.uuid || '',
      availableMsisdns,
    };
  }

  private formatMsisdn(msisdn: string): string {
    // Formatar MSISDN no formato (XX) XXXXX-XXXX se ainda não estiver formatado
    const cleaned = msisdn.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 7)}-${cleaned.substring(7)}`;
    }
    return msisdn;
  }

  onLineSelectionChange(productIndex: number, selectedLines: MultiSelectItem[]): void {
    if (this.products[productIndex]) {
      const product = this.products[productIndex];
      const cappedSelection = selectedLines.slice(0, product.subscriptionCount);
      this.products[productIndex].selectedMsisdns = [...cappedSelection];
      
      this.cdr.detectChanges();

      if (this.isEditing) {
        const msisdns = cappedSelection.map(line => line.label.replace(/\D/g, ''));
        this.cartFacade.updateCartItemLinkedMsisdns(product.cartItemId, msisdns);
      }
    }
  }

  getTotalLinkedLines(): number {
    return this.products.reduce((total, product) => total + product.selectedMsisdns.length, 0);
  }

  getTotalSubscriptions(): number {
    return this.products.reduce((total, product) => total + product.subscriptionCount, 0);
  }

  isFormValid(): boolean {
    if (this.products.length === 0) return false;

    return this.products.every(product => {
      const selectedMsisdns = product.selectedMsisdns || [];
      return selectedMsisdns.length === product.subscriptionCount;
    });
  }

  get hasProducts(): boolean {
    return this.products.length > 0;
  }

  get showCheck(): boolean {
    if (this.isLastStep && !this.isEditing) {
      return true;
    }

    if (this.isEditing) {
      return this.hasProducts && this.isFormValid();
    }

    return this.hasProducts && this.isFormValid();
  }

  getProgressText(product: ProductLinkData): string {
    const count = product.selectedMsisdns.length;
    if (count === 0) {
      return 'Nenhuma linha vinculada';
    }
    return `Você vinculou ${count} ${count === 1 ? 'linha' : 'linhas'} telefônica${count === 1 ? '' : 's'}.`;
  }

  onPreviousStep(): void {
    this.previousStep.emit();
  }

  onNextStep(): void {
    if (this.isFormValid()) {
      this.nextStep.emit();
    }
  }

  editPayment(): void {
    if (this.isLastStep && this.navigateToLinkingStepOnEdit) {
      this.navigateToLinkingStep.emit();
      return;
    }

    if (this.isLastStep) {
      this.saveOriginalValues();
    }

    this.isEditing = true;
    this.editingStateChange.emit(this.isEditing);
  }

  cancelEdit(): void {
    if (this.isLastStep) {
      this.restoreOriginalValues();
    }
    
    this.isEditing = false;
    this.editingStateChange.emit(this.isEditing);
  }

  saveAndContinue(): void {
    if (this.isLastStep && this.isEditing) {
      this.saveMsisdnsToCart();
      this.clearOriginalValues();
    }
    
    this.isEditing = false;
    this.editingStateChange.emit(this.isEditing);
    if (!this.isLastStep) {
      this.nextStep.emit();
    }
  }

  private saveOriginalValues(): void {
    this.products.forEach(product => {
      this.originalSelectedMsisdns[product.cartItemId] = [...product.selectedMsisdns];
    });
  }

  private restoreOriginalValues(): void {
    this.products.forEach(product => {
      if (this.originalSelectedMsisdns[product.cartItemId] !== undefined) {
        product.selectedMsisdns = [...this.originalSelectedMsisdns[product.cartItemId]];
        const msisdns = product.selectedMsisdns.map(line => {
          return line.label.replace(/\D/g, '');
        });
        this.cartFacade.updateCartItemLinkedMsisdns(product.cartItemId, msisdns);
      }
    });
  }

  private clearOriginalValues(): void {
    this.originalSelectedMsisdns = {};
  }

  private saveMsisdnsToCart(): void {
    this.products.forEach(product => {
      const msisdns = product.selectedMsisdns.map(line => {
        return line.label.replace(/\D/g, '');
      });
      this.cartFacade.updateCartItemLinkedMsisdns(product.cartItemId, msisdns);
    });
  }

  getLinkedLinesText(product: ProductLinkData): string {
    if (product.selectedMsisdns.length === 0) {
      return this.i18nService.instant('pages.productLink.viewMode.noLines');
    }
    return product.selectedMsisdns.map(line => line.label).join(', ');
  }
}

